<script setup lang="ts">
import { computed } from 'vue';
import UiTooltip from '@/modules/ui/components/ui/UiTooltip.vue';

const { value } = defineProps<{
  value: number;
  completedTooltip?: string;
}>();

const SIZE = 24;
const STROKE = 3;

const radius = (SIZE - STROKE) / 2 + 1; // +1 исправляет отображение в Safari
const full = 2 * Math.PI * radius;

const progress = computed(() => full - (value / 100) * full);
const isCompleted = computed(() => value === 100);
const color = computed(() => (isCompleted.value ? 'var(--color-green-600)' : 'var(--color-blue-400)'));
</script>

<template>
  <UiTooltip
    class="ui-progress-circle"
    :text="(isCompleted && completedTooltip) || `${value}%`"
    :max-width="220"
  >
    <div
      class="wrapper"
      :class="{ _pulse: isCompleted }"
    >
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        :width="SIZE"
        :height="SIZE"
        :viewBox="`${SIZE / 2} ${SIZE / 2} ${SIZE} ${SIZE}`"
      >
        <circle
          :cx="SIZE"
          :cy="SIZE"
          :r="radius"
          stroke="var(--color-gray-100)"
          :stroke-width="STROKE"
          fill="none"
        />
        <circle
          class="progress"
          :cx="SIZE"
          :cy="SIZE"
          :r="radius"
          fill="none"
          :stroke-width="STROKE"
          :stroke-dasharray="full"
          :stroke-dashoffset="progress"
          stroke-linecap="round"
          :stroke="color"
        />
      </svg>
    </div>
  </UiTooltip>
</template>

<style scoped lang="scss">
.wrapper {
  position: relative;
  z-index: 0;

  border-radius: 50%;

  &._pulse {
    @keyframes pulse-scale {
      0%,
      100% {
        transform: scale(0.9);
      }
      70% {
        transform: scale(1);
      }
    }

    @keyframes pulse-ring {
      0% {
        transform: scale(1);
        opacity: 0.9;
      }
      70%,
      100% {
        transform: scale(1.5);
        opacity: 0;
      }
    }

    animation: pulse-scale 2s infinite;

    &::after {
      content: '';

      position: absolute;
      z-index: -1;
      inset: -1px;

      border-radius: 50%;
      border: 3px solid var(--color-green-600);

      will-change: transform, opacity;
      animation: pulse-ring 2s infinite;
    }
  }
}

.icon {
  overflow: visible; // Для Safari
}

.progress {
  transition:
    stroke var(--transition-slow),
    stroke-dashoffset var(--transition-slow);
}
</style>
